import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Notification } from '../types';
import { notificationTypeConfig } from '../types';
import { useNotifications } from '../hooks/useNotifications';
import { ENV } from '../../../shared/config/env';
import { useWebViewLoading } from '../../../shared/hooks';
import { postMessageToFlutter } from '../../../shared/services/bridge';

import './NotificationList.css';

/**
 * デバッグログをFlutter WebViewに送信
 * REACT_APP_DEBUG環境変数がtrueの場合のみ出力
 */
const sendDebugLog = (message: string): void => {
  if (!ENV.DEBUG) return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webview = (window as any).DebugChannel;
    if (webview?.postMessage) {
      webview.postMessage(message);
    }
    console.log('[NotificationList]', message);
  } catch {
    // WebView外での実行時は無視
  }
};

/**
 * Flutter WebViewのNotificationHandlerにメッセージを送信
 * Inquiryパターンに準拠: Flutter側でローディング表示→WebView遷移を制御
 */
const postToNotificationHandler = (notificationId: string): void => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = (window as any).NotificationHandler;
    if (handler?.postMessage) {
      handler.postMessage(notificationId);
    }
  } catch {
    // WebView外での実行時は無視
  }
};

/**
 * お知らせ行のProps
 */
interface NotificationRowProps {
  notification: Notification;
  onRead: (id: string) => void;
}

/**
 * お知らせ行コンポーネント
 */
const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onRead }) => {
  const isUnread = notification.status === 'unread';
  const typeConfig = notificationTypeConfig[notification.type];

  // 日時をフォーマット
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const handleRowClick = () => {
    // ローディング表示を先に開始（React側で制御）
    postMessageToFlutter('showLoading');
    // 既読処理
    onRead(notification.id);
    // Flutter側のNotificationHandlerに通知IDを送信
    // Flutter側でWebViewを詳細ページに遷移させる
    postToNotificationHandler(notification.id);
  };

  return (
    <div className="notification-row" onClick={handleRowClick}>
      <div className="notification-row-left">
        {isUnread && <span className="unread-indicator" />}
      </div>
      <div className="notification-row-content">
        <div className="notification-row-header">
          <span
            className="notification-type-badge"
            style={{ backgroundColor: typeConfig.color }}
          >
            {typeConfig.label}
          </span>
          <span className="notification-date">{formatDate(notification.createdAt)}</span>
        </div>
        <div className={`notification-title ${isUnread ? 'unread' : ''}`}>
          {notification.title}
        </div>
      </div>
      <div className="notification-row-right">
        <span className="arrow">›</span>
      </div>
    </div>
  );
};

/**
 * お知らせ一覧コンポーネント
 */
function NotificationList() {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId') || undefined;
  const token = searchParams.get('token') || undefined;

  sendDebugLog(`コンポーネント開始: accountId=${accountId}, tokenあり=${!!token}`);

  const { data, unreadCount, isLoading, error, refetch } = useNotifications(accountId, token);

  // 未読件数をrefで保持（handleReadのcallback依存を減らすため）
  const unreadCountRef = useRef(unreadCount);
  unreadCountRef.current = unreadCount;

  sendDebugLog(`データ取得結果: isLoading=${isLoading}, count=${data.length}, unreadCount=${unreadCount}, error=${error?.message || 'なし'}`);

  useEffect(() => {
    document.body.style.overflow = 'auto';
    sendDebugLog('useEffect実行');
  }, []);

  // Flutter WebViewのローディング制御（データ取得完了時 & ページ再表示時）
  // コールバックrefを使用してDOMが実際に描画された後にhideLoadingを呼ぶ
  const contentRef = useWebViewLoading(isLoading);

  const handleRead = useCallback((id: string) => {
    // FlutterにmarkAsReadを通知
    postMessageToFlutter(`markAsRead:${id}`);
    // 未読件数をAPIデータから計算して通知（クリックした項目を既読にするので-1）
    const newUnreadCount = Math.max(0, unreadCountRef.current - 1);
    postMessageToFlutter(`unreadCount:${newUnreadCount}`);
    // 注意: API側では詳細画面表示時に自動で既読化されるため、
    // ここではローカル状態の更新のみ（refetchは不要、詳細から戻った時にFlutter側でrefresh）
  }, []);

  const sortedNotifications = useMemo(
    () => [...data].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ),
    [data]
  );

  // ローディング中はFlutter側のオーバーレイに任せるため、空のコンテナを返す
  if (isLoading) {
    return <div className="notification-container" />;
  }

  return (
    <main ref={contentRef} className="notification-container">
      <h1 className="main-title">お知らせ一覧</h1>
      <hr className="divider" />

      <div className="notification-list">
        {sortedNotifications.length === 0 ? (
          <div className="empty-message">現在お知らせはありません。</div>
        ) : (
          sortedNotifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              onRead={handleRead}
            />
          ))
        )}
      </div>
    </main>
  );
}

export default NotificationList;
